import { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { getEmpleados, cambiarEstatusVacante } from './Integracion';

// Coinciden con TH_VACANTE_ESTATUS
const ESTATUS = [
    { code: 1, name: 'ABIERTA' },
    { code: 2, name: 'EN PROCESO' },
    { code: 3, name: 'CUBIERTA' },
    { code: 4, name: 'CANCELADA' },
];
const ESTATUS_CUBIERTA = 3;
const ESTATUS_FINALES = [3, 4];
const TIPO_REEMPLAZO = 3;

const CambiarEstatusDialog = ({ visible, vacante, onHide, onSaved }) => {
    const [idEstatus, setIdEstatus] = useState(null);
    const [fechaConclusion, setFechaConclusion] = useState(null);
    const [idUsuarioCubre, setIdUsuarioCubre] = useState(null);
    const [empleados, setEmpleados] = useState([]);
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState(null);

    const esFinal = ESTATUS_FINALES.includes(idEstatus);
    // Toda vacante cubierta registra quién la cubrió (ID_USUARIO_CUBRE)
    const requiereCubre = idEstatus === ESTATUS_CUBIERTA;
    const esReemplazo = vacante?.idTipo === TIPO_REEMPLAZO;

    useEffect(() => {
        if (!visible) return;
        setIdEstatus(null);
        setFechaConclusion(null);
        setIdUsuarioCubre(null);
        setError(null);
    }, [visible]);

    // Candidatos: empleados del mismo puesto, sin la persona reemplazada
    useEffect(() => {
        setEmpleados([]);
        if (!requiereCubre) return;

        getEmpleados({ puesto: vacante.idPuesto })
            .then((lista) => setEmpleados(lista.filter((e) => e.code !== vacante.idUsuarioReemplaza)))
            .catch(() => setError('No se pudieron cargar los empleados de este puesto.'));
    }, [requiereCubre]);

    const cambiarEstatus = (valor) => {
        setIdEstatus(valor);
        setFechaConclusion(ESTATUS_FINALES.includes(valor) ? new Date() : null);
        setIdUsuarioCubre(null);
    };

    const completo = idEstatus && (!esFinal || fechaConclusion) && (!requiereCubre || idUsuarioCubre);

    const guardar = async () => {
        setGuardando(true);
        setError(null);
        try {
            await cambiarEstatusVacante(vacante.id, {
                idEstatus,
                fechaConclusion: esFinal ? fechaConclusion.toLocaleDateString('en-CA') : null, // YYYY-MM-DD
                idUsuarioCubre: requiereCubre ? idUsuarioCubre : null,
            });
        } catch (e) {
            console.error('Error al cambiar estatus:', e);
            setError(e.response?.data?.message ?? 'No se pudo cambiar el estatus. Intenta de nuevo.');
            setGuardando(false);
            return;
        }

        setGuardando(false);
        onSaved('Estatus actualizado');
    };

    if (!vacante) return null;

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancelar" text onClick={onHide} disabled={guardando} />
            <Button
                label="Cambiar estatus"
                icon="pi pi-check"
                onClick={guardar}
                loading={guardando}
                disabled={!completo}
            />
        </div>
    );

    return (
        <Dialog
            header={`Estatus de la vacante #${vacante.id}`}
            visible={visible}
            onHide={onHide}
            footer={footer}
            style={{ width: '32rem' }}
            breakpoints={{ '640px': '95vw' }}
            modal
            draggable={false}
        >
            <p className="mt-0 mb-3 text-600">
                {vacante.puesto} · {vacante.cliente} · Estatus actual: <strong>{vacante.estatus}</strong>
            </p>

            {error && <Message severity="error" text={error} className="w-full mb-3" />}

            <div className="formgrid grid">
                <div className="field col-12">
                    <label htmlFor="estatus">Nuevo estatus *</label>
                    <Dropdown
                        inputId="estatus"
                        value={idEstatus}
                        options={ESTATUS.filter((e) => e.code !== vacante.idEstatus)}
                        optionLabel="name"
                        optionValue="code"
                        onChange={(e) => cambiarEstatus(e.value)}
                        placeholder="Selecciona un estatus"
                        className="w-full"
                    />
                </div>

                {requiereCubre && (
                    <div className="field col-12">
                        <label htmlFor="cubre">
                            {esReemplazo ? `¿Quién reemplazó a ${vacante.reemplaza}? *` : '¿Quién cubrió la vacante? *'}
                        </label>
                        <Dropdown
                            inputId="cubre"
                            value={idUsuarioCubre}
                            options={empleados}
                            optionLabel="name"
                            optionValue="code"
                            onChange={(e) => setIdUsuarioCubre(e.value)}
                            placeholder="Selecciona un empleado"
                            emptyMessage="No hay empleados activos en este puesto. Verifica que ya esté dado de alta."
                            filter
                            className="w-full"
                        />
                    </div>
                )}

                {esFinal && (
                    <div className="field col-12">
                        <label htmlFor="fechaConclusion">Fecha de conclusión *</label>
                        <Calendar
                            inputId="fechaConclusion"
                            value={fechaConclusion}
                            onChange={(e) => setFechaConclusion(e.value)}
                            maxDate={new Date()}
                            dateFormat="dd/mm/yy"
                            showIcon
                            className="w-full"
                        />
                    </div>
                )}
            </div>

            {esFinal && (
                <Message
                    severity="warn"
                    text="Una vez cubierta o cancelada, la vacante ya no podrá cambiar de estatus ni editarse."
                    className="w-full"
                />
            )}
        </Dialog>
    );
};

export default CambiarEstatusDialog;